import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { logLogin } from '../services/tgActivity'
import toast from 'react-hot-toast'

/**
 * User profile from subcontractors table
 * All users are TGs, some have admin privileges (is_admin: true)
 */
interface UserProfile {
  id: string
  user_id: string
  email: string
  full_name: string
  name: string // TG name
  role: 'admin' | 'subcontractor' // Derived from is_admin
  is_admin: boolean
  phone?: string
  company?: string
  is_active: boolean
  price_list_id?: string
  color?: string
  created_at: string
  updated_at: string
}

type ViewMode = 'admin' | 'subcontractor'

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: UserProfile | null
  loading: boolean
  // View mode for admins (allows switching between admin and TG view)
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  // Effective role considers viewMode for admins
  effectiveRole: 'admin' | 'subcontractor'
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: AuthError | null }>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Key for persisting view mode
const VIEW_MODE_STORAGE_KEY = 'tsa-view-mode'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Restore view mode from localStorage
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return (saved === 'subcontractor' ? 'subcontractor' : 'admin') as ViewMode
  })

  // Use ref to track current profile to avoid stale closure issues
  const profileRef = useRef<UserProfile | null>(null)

  // Track last login log time to prevent duplicate logging
  const lastLoginLogRef = useRef<number>(0)

  // Keep ref in sync with state
  useEffect(() => {
    profileRef.current = profile
  }, [profile])

  // Persist view mode to localStorage
  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode)
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  }, [])

  // Effective role: for admins, it can be switched via viewMode
  // For non-admins, it's always 'subcontractor'
  const effectiveRole: 'admin' | 'subcontractor' = profile?.is_admin
    ? viewMode
    : 'subcontractor'

  /**
   * Fetch user profile from subcontractors table
   * This is the new model where all users are TGs
   */
  const fetchUserProfile = async (userId: string, retryCount = 0): Promise<UserProfile | null> => {
    const maxRetries = 3
    const retryDelay = 1000

    try {
      // Verify session exists before querying
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()

      if (sessionError || !sessionData?.session) {
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return fetchUserProfile(userId, retryCount + 1)
        }
        return null
      }

      if (sessionData.session.user.id !== userId) {
        return null
      }

      // Query subcontractors table (new model)
      const queryPromise = supabase
        .from('subcontractors')
        .select('*')
        .eq('user_id', userId)
        .single()

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Query timeout after 8 seconds')), 8000)
      )

      const result = await Promise.race([queryPromise, timeoutPromise]) as any
      const { data, error } = result

      if (error) {
        if (error.code === 'PGRST116') {
          // No subcontractor found - try legacy user_profiles table as fallback
          return await fetchLegacyProfile(userId)
        }

        if (retryCount < maxRetries && (error.message?.includes('network') || error.message?.includes('fetch'))) {
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return fetchUserProfile(userId, retryCount + 1)
        }

        return null
      }

      // Transform subcontractor to UserProfile format
      if (data) {
        return {
          id: data.id,
          user_id: data.user_id,
          email: data.email,
          full_name: data.name,
          name: data.name,
          role: data.is_admin ? 'admin' : 'subcontractor',
          is_admin: data.is_admin ?? false,
          phone: data.phone,
          company: data.company,
          is_active: data.is_active ?? true,
          price_list_id: data.price_list_id,
          color: data.color,
          created_at: data.created_at,
          updated_at: data.updated_at
        }
      }

      return null
    } catch (error) {
      console.error('Exception during profile fetch:', error)

      if (retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        return fetchUserProfile(userId, retryCount + 1)
      }

      return null
    }
  }

  /**
   * Fallback: fetch from legacy user_profiles table
   * This provides backward compatibility during migration
   */
  const fetchLegacyProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error || !data) {
        return null
      }

      // Transform legacy profile to new format
      return {
        id: data.id,
        user_id: data.user_id,
        email: data.email,
        full_name: data.full_name,
        name: data.full_name,
        role: data.role as 'admin' | 'subcontractor',
        is_admin: data.role === 'admin',
        phone: data.phone,
        company: data.company,
        is_active: data.is_active ?? true,
        created_at: data.created_at,
        updated_at: data.updated_at
      }
    } catch (error) {
      console.error('Legacy profile fetch failed:', error)
      return null
    }
  }

  // Safely update profile (won't clear existing profile on fetch failure)
  // Returns the fetched profile or null
  const safeUpdateProfile = useCallback(async (userId: string, forceRefresh = false): Promise<UserProfile | null> => {
    const currentProfile = profileRef.current

    if (!forceRefresh && currentProfile && currentProfile.user_id === userId) {
      return currentProfile
    }

    try {
      const userProfile = await fetchUserProfile(userId)

      if (userProfile) {
        setProfile(userProfile)
        profileRef.current = userProfile // Update ref immediately
        // Note: We do NOT reset viewMode here - let localStorage persist it
        // The user's chosen view mode should survive page refreshes
        return userProfile
      } else if (!currentProfile) {
        setProfile(null)
      }
      return null
    } catch (error) {
      console.error('Profile fetch failed:', error)
      return null
    }
  }, [])

  // Manual refresh function exposed to components
  const refreshProfile = useCallback(async () => {
    if (user) {
      await safeUpdateProfile(user.id, true)
    }
  }, [user, safeUpdateProfile])

  // Check for email change confirmation in URL hash
  useEffect(() => {
    const hash = window.location.hash
    if (hash && hash.includes('type=email_change')) {
      // Show message about email change confirmation
      setTimeout(() => {
        toast.success('E-postbekräftelse mottagen! Kom ihåg att bekräfta från båda adresserna.', {
          duration: 5000,
          icon: '✉️'
        })
      }, 500)
      // Clean up the URL hash
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!isMounted) return

      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        await safeUpdateProfile(session.user.id)
      }

      if (isMounted) {
        setLoading(false)
      }
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return

      switch (event) {
        case 'SIGNED_OUT':
          setSession(null)
          setUser(null)
          setProfile(null)
          setLoading(false)
          // Reset view mode on logout
          setViewModeState('admin')
          localStorage.removeItem(VIEW_MODE_STORAGE_KEY)
          break

        case 'TOKEN_REFRESHED':
          setSession(session)
          setUser(session?.user ?? null)
          break

        case 'SIGNED_IN':
          setSession(session)
          setUser(session?.user ?? null)

          if (session?.user) {
            setTimeout(async () => {
              const currentProfile = profileRef.current
              if (!currentProfile || currentProfile.user_id !== session.user.id) {
                const fetchedProfile = await safeUpdateProfile(session.user.id)
                // Log login for non-admin TGs (with debounce to prevent duplicates)
                const now = Date.now()
                const timeSinceLastLog = now - lastLoginLogRef.current
                if (fetchedProfile && !fetchedProfile.is_admin && timeSinceLastLog > 10000) {
                  lastLoginLogRef.current = now
                  logLogin(fetchedProfile.id).catch(err =>
                    console.error('Failed to log TG login:', err)
                  )
                }
              }
            }, 200)
          } else {
            setProfile(null)
          }
          setLoading(false)
          break

        case 'INITIAL_SESSION':
          setSession(session)
          setUser(session?.user ?? null)

          if (session?.user) {
            const currentProfile = profileRef.current
            if (!currentProfile || currentProfile.user_id !== session.user.id) {
              await safeUpdateProfile(session.user.id)
            }
          } else {
            setProfile(null)
          }
          setLoading(false)
          break

        case 'USER_UPDATED':
          setSession(session)
          setUser(session?.user ?? null)

          if (session?.user) {
            const currentProfile = profileRef.current
            // Check if email changed
            if (currentProfile && session.user.email && currentProfile.email !== session.user.email) {
              toast.success('Din e-postadress har ändrats till ' + session.user.email)
              // Refresh profile to get updated email from database
              await safeUpdateProfile(session.user.id, true)
            } else if (!currentProfile || currentProfile.user_id !== session.user.id) {
              await safeUpdateProfile(session.user.id)
            }
          } else {
            setProfile(null)
          }
          setLoading(false)
          break

        case 'PASSWORD_RECOVERY':
          setSession(session)
          setUser(session?.user ?? null)
          setLoading(false)
          break

        default:
          setSession(session)
          setUser(session?.user ?? null)
          setLoading(false)
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [safeUpdateProfile])

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      })

      if (error) {
        return { error }
      }

      return { error: null }
    } catch (error) {
      console.error('Sign in error:', error)
      return { error: { message: 'Unexpected error' } as AuthError }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' })
      if (!error) {
        setSession(null)
        setUser(null)
        setProfile(null)
        setViewModeState('admin')
        localStorage.removeItem(VIEW_MODE_STORAGE_KEY)
      }
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const resetPassword = async (email: string, _captchaToken?: string) => {
    try {
      // Use Edge Function to bypass CAPTCHA requirement
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}/login`,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('Reset password error:', result.error)
        return { error: { message: result.error || 'Kunde inte skicka återställningslänk' } as AuthError }
      }

      return { error: null }
    } catch (error) {
      console.error('Reset password error:', error)
      return { error: { message: 'Unexpected error' } as AuthError }
    }
  }

  const value = {
    user,
    session,
    profile,
    loading,
    viewMode,
    setViewMode,
    effectiveRole,
    signIn,
    signOut,
    resetPassword,
    refreshProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
